import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";
import {
  applyBrandCssVars,
  brandCssVarsFromOrg,
  clearBrandCssVars,
  shouldApplyOrgBrandTheme,
} from "@/lib/brandColors";

/**
 * Applies the selected workspace brand trio to documentElement CSS vars.
 * Skips social feed / no org, and when membership.use_org_branding is false.
 */
export function OrgBrandThemeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const {
    selectedOrgId,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  } = useOrgFilter();
  const [preferLive, setPreferLive] = useState(false);
  const appliedOrgRef = useRef(null);

  useEffect(() => {
    const onBrandChange = () => setPreferLive(true);
    window.addEventListener("circa:org-brand-changed", onBrandChange);
    return () =>
      window.removeEventListener("circa:org-brand-changed", onBrandChange);
  }, []);

  useEffect(() => {
    setPreferLive(false);
  }, [selectedOrgId, workspaceRefreshKey]);

  useEffect(() => {
    return () => {
      clearBrandCssVars(document.documentElement);
      appliedOrgRef.current = null;
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    let cancelled = false;

    const apply = async () => {
      if (
        !isAuthenticated ||
        !selectedOrgId ||
        selectedOrgId === "social" ||
        workspaceUpdating
      ) {
        if (appliedOrgRef.current != null) {
          clearBrandCssVars(root);
          appliedOrgRef.current = null;
        }
        return;
      }

      const snapMatches =
        !preferLive &&
        workspaceSnapshot?.orgId === selectedOrgId &&
        workspaceSnapshot?.org &&
        workspaceSnapshot?.me;

      try {
        let org;
        let me;
        if (snapMatches) {
          org = workspaceSnapshot.org;
          me = workspaceSnapshot.me;
        } else {
          const token = getAccessToken();
          const [orgRes, meRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/org/${selectedOrgId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API_BASE_URL}/org/${selectedOrgId}/me`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          if (cancelled) return;
          org = orgRes.data;
          me = meRes.data;
        }
        if (cancelled) return;
        const useBranding = me?.use_org_branding !== false;
        if (
          !shouldApplyOrgBrandTheme({
            selectedOrgId,
            useOrgBranding: useBranding,
            org,
          })
        ) {
          clearBrandCssVars(root);
          appliedOrgRef.current = null;
          return;
        }
        applyBrandCssVars(root, brandCssVarsFromOrg(org));
        appliedOrgRef.current = selectedOrgId;
      } catch (err) {
        console.error("Failed to apply org brand theme:", err);
      }
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    selectedOrgId,
    preferLive,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  ]);

  return children;
}

/** Notify theme provider that org brand or member preference changed. */
export function notifyOrgBrandChanged() {
  window.dispatchEvent(new Event("circa:org-brand-changed"));
}
